// Formatting + print document for the hourly billing stats page (HourlyBillingStats.vue).

export function formatMinutes(min) {
  const m = Math.round(Number(min) || 0);
  return `${Math.floor(m / 60)}h ${String(m % 60).padStart(2, "0")}min`;
}

function formatMoney(value) {
  return Number(value)
    .toFixed(2)
    .replace(".", ",")
    .replace(/(\d)(?=(\d{3})+(?:,\d+)?$)/g, "$1.");
}

export function formatAmounts(amounts) {
  const keys = Object.keys(amounts || {});
  if (keys.length === 0) return "0,00";
  return keys.map((cur) => `${formatMoney(amounts[cur])} ${cur}`).join(" + ");
}

export function escapeHtml(s) {
  return String(s === null || s === undefined ? "" : s)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
}

export function normalizeForSearch(s) {
  return String(s || "")
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .replace(/đ/gi, "d")
    .toLowerCase();
}

function formatIsoDate(iso) {
  const [y, m, d] = String(iso).split("-");
  return `${d}.${m}.${y}.`;
}

// Describes the filters the backend actually used (response `filters`), so a printed
// header can never name a different period than the numbers below it.
export function describeFilters({ t, filters, serviceTypes }) {
  const service = (serviceTypes || []).find((s) => s.id === filters.serviceTypeId);
  return t("analytics.hourly.period_text", {
    from: formatIsoDate(filters.from),
    to: formatIsoDate(filters.to),
    invoiced: t(`analytics.hourly.invoiced_${filters.invoiced}`),
    service: service ? service.type_name : t("analytics.hourly.service_all"),
  });
}

// Self-contained document: the PDF renderer (puppeteer) gets no app CSS, and every
// interpolated value is escaped because the page is rendered with JS enabled.
export function buildHourlyPrintHtml({ t, stats, lawyers, selected, entries, filterText, searchText, generatedBy, generatedAt }) {
  const e = escapeHtml;
  // A searched print shows fewer rows than the totals cover; say so on the page.
  const searchNote =
    lawyers.length !== stats.lawyers.length
      ? `<br>${e(t("analytics.hourly.showing", { shown: lawyers.length, total: stats.lawyers.length }))}` +
        (searchText ? ` · ${e(t("analytics.hourly.search"))}: „${e(searchText)}“` : "")
      : "";
  const lawyerRows = lawyers
    .map(
      (l) => `<tr><td>${e(l.name)}</td><td class="n">${formatMinutes(l.minutes)}</td><td class="n">${e(formatAmounts(l.amounts))}</td>
        <td class="n">${l.entries}</td><td class="n">${l.bills}</td><td class="n">${l.cases}</td><td class="n">${formatMinutes(l.participated_minutes)}</td></tr>`
    )
    .join("");
  const unassignedRow =
    stats.unassigned.entries > 0
      ? `<tr><td><i>${e(t("analytics.hourly.unassigned"))}</i></td><td class="n">${formatMinutes(stats.unassigned.minutes)}</td>
         <td class="n">${e(formatAmounts(stats.unassigned.amounts))}</td><td class="n">${stats.unassigned.entries}</td><td></td><td></td><td></td></tr>`
      : "";
  const warnings = [];
  if (stats.warnings.possible_duplicate_entries > 0)
    warnings.push(e(t("analytics.hourly.warn_duplicates", { n: stats.warnings.possible_duplicate_entries })));
  if (stats.warnings.zero_rate_entries > 0)
    warnings.push(e(t("analytics.hourly.warn_zero_rate", { n: stats.warnings.zero_rate_entries })));

  const detail = selected
    ? `<h2>${e(t("analytics.hourly.detail_for", { name: selected.name }))}</h2>
      <table><thead><tr><th>${e(t("analytics.hourly.col_date"))}</th><th>${e(t("analytics.hourly.col_case"))}</th>
      <th>${e(t("analytics.hourly.col_bill"))}</th><th>${e(t("analytics.hourly.col_service"))}</th>
      <th>${e(t("analytics.hourly.col_description"))}</th><th>${e(t("analytics.hourly.col_time"))}</th>
      <th>${e(t("analytics.hourly.col_rate"))}</th><th>${e(t("analytics.hourly.col_amount"))}</th>
      <th>${e(t("analytics.hourly.col_role"))}</th><th>${e(t("analytics.hourly.col_invoiced"))}</th></tr></thead><tbody>
      ${entries
        .map(
          (x) => `<tr${x.possible_duplicate ? ' class="dup"' : ""}><td>${e(x.date)}</td><td>${e(x.broj_predmeta)}</td>
          <td>${e(x.troskovnik_name)}</td><td>${e(x.service_type_name)}</td><td>${e(x.description)}</td>
          <td class="n">${formatMinutes(x.minutes)}</td><td class="n">${e(formatAmounts({ [x.currency]: Number(x.satnica) || 0 }))}</td>
          <td class="n">${x.role === "main" ? e(formatAmounts({ [x.currency]: x.amount })) : "—"}</td>
          <td>${e(t(x.role === "main" ? "analytics.hourly.role_main" : "analytics.hourly.role_participant"))}</td>
          <td>${e(t(x.is_invoiced ? "analytics.hourly.yes" : "analytics.hourly.no"))}</td></tr>`
        )
        .join("")}</tbody></table>`
    : "";

  return `<!doctype html><html><head><meta charset="utf-8"><style>
    body{font-family:Arial,Helvetica,sans-serif;font-size:11px;color:#222;margin:24px}
    h1{font-size:18px;margin:0 0 4px} h2{font-size:14px;margin:18px 0 6px}
    .meta{color:#555;margin-bottom:12px} .warn{color:#a15c00;margin:4px 0}
    table{width:100%;border-collapse:collapse;margin-top:6px} th,td{border:1px solid #ccc;padding:4px 6px;text-align:left;vertical-align:top}
    th{background:#f0efff} td.n{text-align:right;white-space:nowrap} tr.total td{font-weight:bold;background:#fafafa}
    tr.dup td{background:#fff4e0} thead{display:table-header-group} tr{page-break-inside:avoid}
  </style></head><body>
    <h1>${e(t("analytics.hourly.title"))}</h1>
    <div class="meta">${e(filterText)}${searchNote}<br>${e(t("analytics.hourly.generated", { by: generatedBy, at: generatedAt }))}</div>
    ${warnings.map((w) => `<div class="warn">⚠ ${w}</div>`).join("")}
    <table><thead><tr><th>${e(t("analytics.hourly.col_lawyer"))}</th><th>${e(t("analytics.hourly.col_hours"))}</th>
      <th>${e(t("analytics.hourly.col_amount"))}</th><th>${e(t("analytics.hourly.col_entries"))}</th>
      <th>${e(t("analytics.hourly.col_bills"))}</th><th>${e(t("analytics.hourly.col_cases"))}</th>
      <th>${e(t("analytics.hourly.col_participated"))}</th></tr></thead>
      <tbody>${lawyerRows}${unassignedRow}
      <tr class="total"><td>${e(t("analytics.hourly.total_all"))}</td><td class="n">${formatMinutes(stats.totals.minutes)}</td>
        <td class="n">${e(formatAmounts(stats.totals.amounts))}</td><td class="n">${stats.totals.entries}</td>
        <td class="n">${stats.totals.bills}</td><td class="n">${stats.totals.cases}</td><td></td></tr></tbody></table>
    ${detail}
  </body></html>`;
}
